/**
 * Parameter List Component
 * Displays collapsible parameter rows with status pills
 */

import React, { useState } from 'react';
import StatusPill from './StatusPill';
import { apiService } from '../../../services/api';

interface Parameter {
    group_id: string;
    name: string;
    score: number;
    status: 'optimal' | 'needs_attention';
    tab: string;
}

interface ParameterListProps {
    parameters: Parameter[];
    summary?: {
        total: number;
        optimal: number;
        needs_attention: number;
    };
    tab: string;
    userId: string;
}

const ParameterList: React.FC<ParameterListProps> = ({ parameters, summary, tab, userId }) => {
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [details, setDetails] = useState<any>(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    const handleToggle = async (groupId: string) => {
        if (expandedId === groupId) {
            setExpandedId(null);
            setDetails(null);
            return;
        }

        setExpandedId(groupId);
        setLoadingDetails(true);

        try {
            const result = await apiService.getAsIsParameterDetails(userId, tab, groupId);
            setDetails(result?.data);
        } catch (err) {
            console.error('Error loading details:', err);
        } finally {
            setLoadingDetails(false);
        }
    };

    if (!parameters.length) {
        return (
            <div className="parameter-list empty">
                <div className="empty-message">
                    <span className="empty-icon">📋</span>
                    <p>No parameters match the current filter</p>
                </div>
            </div>
        );
    }

    return (
        <div className="parameter-list">
            {/* Summary Bar */}
            {summary && (
                <div className="parameters-summary-bar">
                    <span className="summary-item">
                        <span className="summary-count">{summary.total}</span> Total
                    </span>
                    <span className="summary-item optimal">
                        <span className="summary-count">{summary.optimal}</span> Optimal
                    </span>
                    <span className="summary-item needs-attention">
                        <span className="summary-count">{summary.needs_attention}</span> Needs Attention
                    </span>
                </div>
            )}

            {/* Parameter Rows */}
            <div className="parameter-rows">
                {parameters.map((param) => (
                    <div
                        key={param.group_id}
                        className={`parameter-row ${expandedId === param.group_id ? 'expanded' : ''}`}
                    >
                        <div
                            className="parameter-header"
                            onClick={() => handleToggle(param.group_id)}
                        >
                            <div className="parameter-info">
                                <span className={`expand-icon ${expandedId === param.group_id ? 'expanded' : ''}`}>
                                    ▶
                                </span>
                                <span className="parameter-name">{param.name}</span>
                            </div>
                            <div className="parameter-meta">
                                <span className="parameter-score">{param.score.toFixed(0)}%</span>
                                <StatusPill status={param.status} />
                            </div>
                        </div>

                        {/* Expanded Details */}
                        {expandedId === param.group_id && (
                            <div className="parameter-details">
                                {loadingDetails ? (
                                    <div className="details-loading">Loading details...</div>
                                ) : details ? (
                                    <>
                                        <p className="details-description">{details.description}</p>

                                        {details.sub_parameters?.length > 0 && (
                                            <div className="sub-parameters">
                                                <h4>Sub-parameters</h4>
                                                <ul>
                                                    {details.sub_parameters.map((sub: string) => (
                                                        <li key={sub}>{sub.replace(/_/g, ' ')}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {details.recommendations?.length > 0 && (
                                            <div className="recommendations">
                                                <h4>Recommendations</h4>
                                                <ul>
                                                    {details.recommendations.map((rec: string, idx: number) => (
                                                        <li key={idx}>{rec}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <p className="details-empty">No details available</p>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ParameterList;
